import { DvToolbarAction } from '@devinco/ui/grid';
import {
  Component,
  OnInit,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { CopyReportDialogComponent } from '@pages/reports-page/components/copy-report-dialog/copy-report-dialog.component';
import { IRowNode, SelectionChangedEvent } from 'ag-grid-community';
import { combineLatest, switchMap, take, tap } from 'rxjs';
import { SendNowTriggerService } from '@pages/reports-page/components/report-tabs/report-information-tab/services/send-now-trigger.service';
import { ReportsGridService } from './reports-grid.service';
import { DialogService } from '@core/services/dialog.service';
import { HttpContext } from '@angular/common/http';
import { SKIP_SUCCESS_INTERCEPTOR } from '@core/interceptors/http-context-tokens';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarConfig } from '@shared/constants/snackbar-config';

@Component({
  selector: 'app-reports-grid',
  templateUrl: './reports-grid.component.html',
  styleUrls: ['./reports-grid.component.scss'],
  providers: [GridOptionsService, ReportsGridService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ReportsGridComponent implements OnInit {
  private readonly gridOptionsService = inject(GridOptionsService);
  private reportsGridService = inject(ReportsGridService);
  private dialog = inject(DialogService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private api = inject(RestApiService);
  private entityService = inject(SplitterWithGridHelperService);
  private sendNowTriggerService = inject(SendNowTriggerService);
  private snackbar = inject(DvSnackbarService);
  private translate = inject(TranslateService);

  actions = signal<DvToolbarAction[]>([]);
  gridOptions: GridOptions;

  private selectedNode: IRowNode;
  private canEdit: boolean;
  private canDelete: boolean;

  ngOnInit(): void {
    combineLatest([
      this.permissionsService.hasPermissions([RolesAndPermissions.ReportEdit]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.ReportDelete,
      ]),
    ])
      .pipe(take(1))
      .subscribe(([canEdit, canDelete]) => {
        this.canEdit = canEdit;
        this.canDelete = canDelete;
        this.gridOptions = this.gridOptionsService.clientSide(
          this.reportsGridService.colDefs,
          Entity.ReportSetting
        );
        this.actions.set(
          this.reportsGridService.getToolbarActions(
            this.openCopyReportModalFn,
            this.sendNowFn
          )
        );
      });
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    this.selectedNode = api.getSelectedNodes()[0];

    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'delete') {
          return {
            ...action,
            disabled: !this.canDelete || !this.selectedNode,
          };
        }
        if (action.icon === 'edit') {
          return { ...action, disabled: !this.selectedNode };
        }
        if (action.icon === 'copy') {
          return {
            ...action,
            disabled: !this.canEdit || !this.selectedNode,
          };
        }
        if (action.icon === 'email') {
          return { ...action, disabled: !this.selectedNode };
        }
        return action;
      })
    );
  }

  private sendNowFn = (): void => {
    this.api
      .upsert(
        Entity.ReportSetting,
        this.selectedNode.data.ReportSettingID,
        {
          SendNow: true,
        },
        undefined,
        new HttpContext().set(SKIP_SUCCESS_INTERCEPTOR, true)
      )
      .pipe(
        tap(() => {
          this.snackbar.open(
            this.translate.instant('Reports.ReportSentSuccessfully'),
            SnackBarConfig.Success
          );
        }),
        switchMap(() =>
          this.entityService.refreshRowData(this.selectedNode.id!)
        ),
        take(1)
      )
      .subscribe(_ => {
        this.sendNowTriggerService.sendReportNowClicked$.next(
          this.selectedNode.data.ReportSettingID
        );
      });
  };

  private openCopyReportModalFn = (): void => {
    this.dialog.open<{ entityId: string }, CopyReportDialogComponent>({
      size: 'sm',
      contentComponent: CopyReportDialogComponent,
      hideFooter: true,
      title: 'Report.CopyModal.Title',
      confirmText: 'General.Save',
      cancelText: 'General.Cancel',
      data: { entityId: this.selectedNode.data.ReportSettingID },
    });
  };
}
